<template>
  <div class="archive training-plans tab-content">
    <spinner-loader :loadingStuff="loadingStuff" />

    <transition name="library-toast">
      <div v-if="toast.show" class="library-toast" :class="`library-toast--${toast.type}`" role="status">
        <i :class="toast.type === 'error' ? 'bi bi-exclamation-circle' : 'bi bi-check-circle'"></i>
        {{ toast.message }}
      </div>
    </transition>

    <!-- Library top bar -->
    <div class="library-top-bar">
      <button v-if="userData.userType === 'Admin' && libraryTab === 'programmes'" @click="openProgrammeModal('create')"
        class="green-button">
        <i class="bi bi-plus-lg"></i> Create Programme
      </button>
      <button v-if="userData.userType === 'Admin' && libraryTab === 'drills'" @click="showEditAddProgrammeModal()"
        class="green-button">
        <i class="bi bi-plus-lg"></i> Add new drill
      </button>
    </div>

    <!-- Full-width tabs -->
    <div class="library-type-tabs">
      <button :class="['lib-tab', { active: libraryTab === 'programmes' }]" @click="switchLibraryTab('programmes')">
        <i class="bi bi-collection"></i> Programmes
      </button>
      <button :class="['lib-tab', { active: libraryTab === 'drills' }]" @click="switchLibraryTab('drills')">
        <i class="bi bi-clipboard-check"></i> Drills
      </button>
    </div>

    <!-- ── Drills tab ──────────────────────────────────────────────────────── -->
    <div v-show="libraryTab === 'drills'">

      <div class="library-header">

        <div class="search-filter">
          <div class="search-box" v-if="trainingPlanData.length > 0 || searchQuery">
            <i class="bi bi-search"></i>
            <input type="text" v-model="searchQuery" placeholder="Search drills..." @input="onSearchInput" />
            <button v-if="searchQuery" class="clear-search" @click="clearSearch">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <button class="filter-toggle-btn" :class="{ active: showFilters || hasActiveFilters() }"
            @click="showFilters = !showFilters">
            <i class="bi bi-funnel"></i> Filters
            <span v-if="hasActiveFilters()" class="filter-count-badge">!</span>
          </button>
          <div v-if="trainingPlanData.length > 0">
            <sorting-by-created-date :arrayDataForSorting="trainingPlanData" sortingByValue="created_date"
              @sorted-data-call="sortedData" />
          </div>
        </div>
      </div>

      <!-- Filter Bar -->
      <div v-if="showFilters" class="filter-bar">
        <div class="filter-row">
          <div class="filter-item">
            <label>Category</label>
            <select v-model="filterCategory" @change="onFilterCategoryChange">
              <option value="">All Categories</option>
              <option v-for="cat in Object.keys(categoryTree)" :key="cat" :value="cat">{{ cat }}</option>
            </select>
          </div>
          <div class="filter-item">
            <label>Sub-category</label>
            <select v-model="filterSubCategory" @change="filterTrainingPlans" :disabled="!filterCategory">
              <option value="">All Sub-categories</option>
              <option v-for="sub in availableSubCategories()" :key="sub" :value="sub">{{ sub }}</option>
            </select>
          </div>
          <div class="filter-item">
            <label>Type</label>
            <select v-model="filterType" @change="filterTrainingPlans">
              <option value="">All Types</option>
              <option v-for="t in trainingTypes" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          <div class="filter-item">
            <label>Level</label>
            <select v-model="filterLevel" @change="filterTrainingPlans">
              <option value="">All Levels</option>
              <option v-for="l in ageLevels" :key="l" :value="l">{{ l }}</option>
            </select>
          </div>
          <div class="filter-item">
            <label>Position</label>
            <select v-model="filterPosition" @change="filterTrainingPlans">
              <option value="">All Positions</option>
              <option v-for="p in positionOptions" :key="p" :value="p">{{ p }}</option>
            </select>
          </div>
        </div>
        <button v-if="hasActiveFilters()" class="clear-filters-btn" @click="clearAllFilters">
          <i class="bi bi-x-circle"></i> Clear All Filters
        </button>
      </div>

      <template v-if="filteredTrainingPlanData.length > 0">
        <div class="training-plan-grid">
          <DrillCard v-for="session in filteredTrainingPlanData" :key="session.id" :drill="session"
            :board="(!session.imageURL && cardBoards[session.id]) || null" @open="openTrainingInfoModel">
            <template #actions>
              <div v-if="userData.userType === 'Admin'" class="card-actions">
                <button @click="showEditAddProgrammeModal('edit', session)" class="btn btn-sm btn-outline-primary"
                  title="Edit">
                  <i class="bi bi-pencil"></i>
                </button>
                <button @click="duplicatePlan(session.id)" class="btn btn-sm btn-outline-info" title="Duplicate">
                  <i class="bi bi-copy"></i>
                </button>
                <button @click="deletetrainingPlan(session.id, session.imageURL)" class="btn btn-sm btn-outline-danger"
                  title="Delete">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </template>
          </DrillCard>
        </div>

        <div v-if="trainingPlanData.length < drillsTotal" class="load-more-wrap">
          <button class="btn btn-outline-primary" @click="loadMoreDrills" :disabled="loadingMoreDrills">
            <i class="bi bi-arrow-down-circle"></i>
            {{ loadingMoreDrills ? 'Loading...' : `Load more (${trainingPlanData.length} of ${drillsTotal})` }}
          </button>
        </div>
      </template>
      <template v-else>
        <div class="empty-state">
          <img src="./img/emptyStateIllustration.svg" />
          <p v-if="searchQuery">No drills matching "{{ searchQuery }}"</p>
          <p v-else-if="hasActiveFilters()">No drills match the selected filters</p>
          <p v-else>No drills in the library yet</p>
        </div>
      </template>

    </div><!-- end drills tab -->

    <!-- ── Programmes tab ────────────────────────────────────────────────── -->
    <div v-show="libraryTab === 'programmes'">

      <div v-if="programmes.length > 0" class="search-box programme-search">
        <i class="bi bi-search"></i>
        <input type="text" v-model="programmeSearchQuery" placeholder="Search programmes..." />
        <button v-if="programmeSearchQuery" class="clear-search" @click="programmeSearchQuery = ''">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div v-if="programmes.length > 0" class="programme-category-filter">
        <button :class="['category-pill', { active: !programmeFilterCategory }]"
          @click="programmeFilterCategory = ''">All</button>
        <button v-for="cat in programmeCategories" :key="cat"
          :class="['category-pill', { active: programmeFilterCategory === cat }]"
          @click="programmeFilterCategory = cat">{{
          cat }}</button>
      </div>

      <template v-if="filteredProgrammes.length > 0">
          <div class="programme-grid">
            <div v-for="programme in filteredProgrammes" :key="programme.id" class="programme-card"
              @click="openViewProgramme(programme)">
              <div class="programme-card-body">
                <h3 class="programme-title">{{ programme.title }}</h3>
                <p v-if="programme.description" class="programme-description">{{ truncateText(programme.description,
                  100) }}</p>
                <div class="programme-meta-row">
                  <span v-if="programme.category" class="prog-badge prog-badge-category"><i class="bi bi-folder"></i> {{
                    programme.category }}</span>
                  <span v-if="programme.area" class="prog-badge"><i class="bi bi-geo-alt"></i> {{ programme.area
                    }}</span>
                  <span v-if="programme.session_time" class="prog-badge"><i class="bi bi-clock"></i> {{
                    programme.session_time }}</span>
                  <span v-if="programme.num_players" class="prog-badge"><i class="bi bi-people"></i> {{
                    programme.num_players }}</span>
                </div>
                <div v-if="programme.equipment && programme.equipment.length" class="equipment-chips">
                  <span v-for="item in programme.equipment" :key="item" class="equipment-chip">{{ item }}</span>
                </div>
                <div class="programme-footer">
                  <span class="drill-count-pill"><i class="bi bi-clipboard-check"></i> {{ programme.drill_count || 0 }}
                    drills</span>
                  <span v-if="programme.created_by_name" class="programme-created-by">{{ programme.created_by_name
                    }}</span>
                </div>
              </div>
              <div v-if="userData.userType === 'Admin'" class="card-actions" @click.stop>
                <button @click="openProgrammeModal('edit', programme)" class="btn btn-sm btn-outline-primary"
                  title="Edit">
                  <i class="bi bi-pencil"></i>
                </button>
                <button @click="duplicateProgramme(programme.id)" class="btn btn-sm btn-outline-info" title="Duplicate">
                  <i class="bi bi-copy"></i>
                </button>
                <button @click="deleteProgramme(programme.id)" class="btn btn-sm btn-outline-danger" title="Delete">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
      </template>

      <div v-else-if="programmeFilterCategory && programmes.length > 0" class="empty-state">
        <img src="./img/emptyStateIllustration.svg" />
        <p>No programmes in "{{ programmeFilterCategory }}"</p>
        <button class="btn btn-outline-primary btn-sm" @click="programmeFilterCategory = ''">Show all
          programmes</button>
      </div>

      <div v-else class="empty-state">
        <img src="./img/emptyStateIllustration.svg" />
        <p>No programmes yet. Create your first programme to get started.</p>
      </div>
    </div><!-- end programmes tab -->

    <!-- Session Detail Modal -->
    <div v-if="showSessionModal" class="modal-overlay session-modal-overlay" @click.self="showSessionModal = false">
      <div class="modal-dialog modal-xl session-detail-modal" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h3>{{ sessionDataModal.name }}</h3>
            <button @click="showSessionModal = false" class="close-button">&times;</button>
          </div>
          <div class="modal-body">
            <ul class="tabs">
              <li class="tab-item">
                <a :class="selectedSessionTab === 'details' ? 'tab-link active' : 'tab-link'"
                  @click="tabChange('selectedSessionTab', 'details')" href="javascript:;">Details</a>
              </li>
              <!-- Boards now shown inline below details -->
              <li class="tab-item">
                <a :class="selectedSessionTab === 'comments' ? 'tab-link active' : 'tab-link'"
                  @click="tabChange('selectedSessionTab', 'comments')" href="javascript:;"><span class="number-alert"
                    v-if="commentsData.length > 0">{{ commentsData.length }}</span>Comments</a>
              </li>

            </ul>

            <div class="tabs-wrapper">
              <div class="tab-content no-flex" v-if="selectedSessionTab === 'details'">
                <div v-if="sessionDataModal.imageURL !== ''" class="large-training-image-wrapper">
                  <img class="large-training-image" :src="getImgUrl(sessionDataModal.imageURL)" alt="" />
                </div>

                <div v-if="sessionDataModal.pdf_url" class="training-pdf-link">
                  <a :href="getFilePath(sessionDataModal.pdf_url)" target="_blank" rel="noopener"
                    class="btn btn-sm btn-outline-danger">
                    <i class="bi bi-file-pdf"></i> View PDF
                  </a>
                </div>

                <div class="training-details-content">
                  <h4>Description</h4>
                  <p>{{ sessionDataModal.introText }}</p>
                  <div v-html="sessionDataModal.details"></div>

                  <!-- Linked Exercises Display -->
                  <div v-if="sessionDataModal.exercises && sessionDataModal.exercises.length > 0"
                    class="linked-exercises-display">
                    <h4><i class="bi bi-journal-bookmark"></i> Exercises ({{ sessionDataModal.exercises.length }})</h4>
                    <div class="exercise-cards">
                      <div v-for="exercise in sessionDataModal.exercises" :key="exercise.id" class="exercise-card">
                        <div class="exercise-card-header">
                          <span class="exercise-name">{{ exercise.exercise_name }}</span>
                          <span :class="['badge', `badge-${exercise.difficulty}`]">{{ exercise.difficulty }}</span>
                        </div>
                        <div class="exercise-card-meta">
                          <span class="category">{{ formatCategory(exercise.category) }}</span>
                          <span v-if="exercise.sets_reps" class="sets-reps">
                            <i class="bi bi-repeat"></i> {{ exercise.sets_reps }}
                          </span>
                          <span v-if="exercise.duration" class="duration">
                            <i class="bi bi-clock"></i> {{ exercise.duration }}
                          </span>
                        </div>
                        <p v-if="exercise.description" class="exercise-description">{{ exercise.description }}</p>
                        <div v-if="exercise.precautions" class="exercise-precautions">
                          <i class="bi bi-exclamation-triangle"></i>
                          <span>{{ exercise.precautions }}</span>
                        </div>
                        <div v-if="exercise.video_url || exercise.video_file_path" class="exercise-video">
                          <a v-if="exercise.video_url" :href="exercise.video_url" target="_blank"
                            class="btn btn-sm btn-outline-primary">
                            <i class="bi bi-play-circle"></i> Watch Video
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="notes" v-if="sessionDataModal.notes">
                    <h4>Notes</h4>
                    <p>{{ sessionDataModal.notes }}</p>
                  </div>

                  <!-- Tactical Boards (inline below details) -->
                  <div class="tactical-boards-inline">
                    <div class="tb-tab-header">
                      <h4><i class="bi bi-easel2"></i> Boards <span v-if="linkedTacticalBoards.length > 0"
                          class="number-alert">{{ linkedTacticalBoards.length }}</span></h4>
                      <button
                        v-if="userData.userType === 'Admin' || userData.userType === 'Coach' || userData.userType === 'HeadCoach'"
                        class="btn btn-sm btn-outline-primary" @click="showTacticalBoardPicker = true">
                        <i class="bi bi-plus-lg"></i> Add Board
                      </button>
                    </div>

                    <div v-if="loadingTacticalBoards" class="text-center py-4">
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                      <span class="ms-2 text-muted">Loading boards...</span>
                    </div>

                    <div v-else-if="linkedTacticalBoards.length === 0" class="tb-empty-state">
                      <i class="bi bi-easel2"></i>
                      <p>No boards linked to this training plan yet.</p>
                      <button
                        v-if="userData.userType === 'Admin' || userData.userType === 'Coach' || userData.userType === 'HeadCoach'"
                        class="btn btn-sm btn-outline-primary" @click="showTacticalBoardPicker = true">
                        <i class="bi bi-plus-lg"></i> Add Board
                      </button>
                    </div>

                    <div v-else class="tb-board-list">
                      <div v-for="(board, idx) in linkedTacticalBoards" :key="board.id" class="tb-board-item">
                        <div class="tb-board-card-header">
                          <div class="tb-board-meta">
                            <span class="tb-board-title"><i class="bi bi-easel2"></i> {{ board.title || 'Board'
                              }}</span>
                            <span v-if="board.category" class="tb-category-badge" :class="'tb-cat-' + board.category">{{
                              board.category }}</span>
                          </div>
                          <button
                            v-if="userData.userType === 'Admin' || userData.userType === 'Coach' || userData.userType === 'HeadCoach'"
                            class="btn btn-sm btn-outline-danger tb-remove-btn"
                            @click="unlinkTacticalBoard(board.id, idx)" title="Remove board">
                            <i class="bi bi-x-lg"></i>
                          </button>
                        </div>
                        <div class="tb-board-preview" @click="viewingTacticalBoard = board" style="cursor: pointer;"
                          title="Click to expand">
                          <TacticalBoardCanvas :title="board.title" :notes="board.notes || ''"
                            :savedElements="board.elements || []" :background="board.background || 'full-pitch'"
                            :savedBackgroundImage="board.background_image || ''"
                            :orientation="board.orientation || 'portrait'"
                            :imageTransform="board.image_transform || { x: 0, y: 0, scale: 1 }" :editMode="false" />
                        </div>
                        <div v-if="board.custom_notes" class="tb-board-notes">
                          <i class="bi bi-chat-left-text"></i> {{ board.custom_notes }}
                        </div>
                      </div>
                    </div>

                    <!-- Tactical Board Picker Modal -->
                    <TacticalBoardPicker :show="showTacticalBoardPicker"
                      :alreadyLinkedIds="linkedTacticalBoards.map(b => b.id)" defaultCategory="training"
                      @close="showTacticalBoardPicker = false" @boards-selected="onTacticalBoardsSelected" />
                  </div>
                </div>
              </div>
              <div class="tab-content" v-if="selectedSessionTab === 'comments'">
                <CommentsComp @getComments="getComments" :commentsData="commentsData" :userAndTypeIds="{
                  user_id: sessionDataModal.userId,
                  type_id: sessionDataModal.id,
                  teamId: 0,
                  clubId: userData.clubId,
                }" />
              </div>

              <!-- Tactical boards moved inline to details tab -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tactical Board Viewer Modal -->
    <div v-if="viewingTacticalBoard" class="modal-overlay tb-viewer-overlay" @click.self="viewingTacticalBoard = null">
      <div class="modal-dialog tb-viewer-dialog" @click.stop>
        <div class="modal-content tb-viewer-content">
          <div class="modal-header">
            <h3><i class="bi bi-easel2"></i> {{ viewingTacticalBoard.title || 'Tactical Board' }}</h3>
            <button @click="viewingTacticalBoard = null" class="close-button">&times;</button>
          </div>
          <div class="modal-body tb-viewer-body">
            <TacticalBoard :board="viewingTacticalBoard"
              :showActions="userData.userType === 'Admin' || userData.userType === 'Coach' || userData.userType === 'HeadCoach'"
              @saved="onTacticalBoardSaved" @deleted="onTacticalBoardDeleted" />
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Programme Modal -->
    <div v-if="showAddEditProgramme" class="modal-overlay" @click.self="closeAddEditModal">
      <div class="modal-dialog modal-lg add-edit-modal" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h3>{{ editMode === false ? 'Add new drill' : 'Edit drill' }}</h3>
            <button @click="closeAddEditModal" class="close-button">&times;</button>
          </div>
          <div class="modal-body">
            <form class="form" @submit.prevent="addEditProgrammeSubmit()">
              <div class="form__item">
                <label class="form__label" for="selectSection">Select type of drill</label>
                <select v-model="newSessionData.section" id="selectSection">
                  <option disabled value="">Please select a type</option>
                  <option v-for="sectionName in sectionOptions" :key="sectionName" :value="sectionName">
                    {{ sectionName }}
                  </option>
                </select>
              </div>

              <!-- Classification Fields -->
              <div class="form-classification-grid">
                <div class="form__item">
                  <label class="form__label">Category</label>
                  <select v-model="newSessionData.category" @change="onFormCategoryChange">
                    <option value="">-- Select Category --</option>
                    <option v-for="cat in Object.keys(categoryTree)" :key="cat" :value="cat">{{ cat }}</option>
                  </select>
                </div>
                <div class="form__item">
                  <label class="form__label">Sub-category</label>
                  <select v-model="newSessionData.sub_category" :disabled="!newSessionData.category">
                    <option value="">-- Select Sub-category --</option>
                    <option v-for="sub in formSubCategories()" :key="sub" :value="sub">{{ sub }}</option>
                  </select>
                </div>
                <div class="form__item">
                  <label class="form__label">Training Type</label>
                  <select v-model="newSessionData.training_type">
                    <option value="">-- Select Type --</option>
                    <option v-for="t in trainingTypes" :key="t" :value="t">{{ t }}</option>
                  </select>
                </div>
                <div class="form__item">
                  <label class="form__label">Age Level</label>
                  <select v-model="newSessionData.age_level">
                    <option value="">-- Select Level --</option>
                    <option v-for="l in ageLevels" :key="l" :value="l">{{ l }}</option>
                  </select>
                </div>
                <div class="form__item">
                  <label class="form__label">Position</label>
                  <select v-model="newSessionData.position">
                    <option value="">-- Select Position --</option>
                    <option v-for="p in positionOptions" :key="p" :value="p">{{ p }}</option>
                  </select>
                </div>
              </div>

              <!-- Assignment Mode Toggle -->
              <div class="form__item">
                <label class="form__label">Assign to</label>
                <div class="assignment-mode-toggle">
                  <button type="button"
                    :class="['btn btn-sm', assignmentMode === 'individual' ? 'btn-primary' : 'btn-outline-primary']"
                    @click="assignmentMode = 'individual'">
                    <i class="bi bi-person"></i> Individual Players
                  </button>
                  <button type="button"
                    :class="['btn btn-sm', assignmentMode === 'team' ? 'btn-primary' : 'btn-outline-primary']"
                    @click="assignmentMode = 'team'">
                    <i class="bi bi-people"></i> By Team
                  </button>
                </div>
              </div>

              <!-- Individual Assignment -->
              <div class="form__item" v-if="assignmentMode === 'individual'">
                <label class="form__label" for="userSelect">Select players</label>
                <div class="select-all-wrapper">
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllUsers">Select
                    All</button>
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllUsers">Deselect
                    All</button>
                </div>
                <div class="user-select-wrapper">
                  <div v-for="user in coachesUsersData" :key="user.id">
                    <input @change="userIdsAddRemove($event)" type="checkbox" :id="user.id" :name="user.id"
                      :value="user.name" :checked="user.selected" />
                    <label :for="user.id">{{ user.name }}</label>
                  </div>
                </div>
              </div>

              <!-- Team Assignment -->
              <div class="form__item" v-if="assignmentMode === 'team'">
                <label class="form__label">Select teams</label>
                <div class="user-select-wrapper" v-if="availableTeams.length > 0">
                  <div v-for="team in availableTeams" :key="team.id">
                    <input type="checkbox" :id="'team-' + team.id" :value="team.id" v-model="selectedTeamIds" />
                    <label :for="'team-' + team.id">{{ team.teamName }}</label>
                  </div>
                </div>
                <p v-else class="text-muted">No teams available</p>
              </div>

              <div class="form__item">
                <label class="form__label" for="imageURL">Upload image</label>
                <input accept="image/jpeg|image/jpg|image/heic|.jpeg"
                  @change="imageChange($event, 'imageFile', 'image', 'fileOne')" type="file" class="form__input"
                  ref="fileOne" id="imageURL" />
              </div>
              <div v-if="image.path !== ''" class="image-preview-wrapper">
                <img class="image-preview" :src="getPath(image.path, 'image')" />
                <button type="button" @click="removeFileFromUI()" class="remove-file-button">Remove file</button>
              </div>
              <div class="form__item">
                <label class="form__label" for="drillPdf">Upload PDF</label>
                <input accept="application/pdf,.pdf" @change="pdfChange($event)" type="file" class="form__input"
                  ref="drillPdf" id="drillPdf" :disabled="pdfUploading" />
                <p v-if="pdfUploading" class="pdf-uploading"><i class="bi bi-arrow-repeat spin"></i> Uploading PDF…</p>
              </div>
              <div v-if="newSessionData.pdf_url" class="pdf-attached">
                <a :href="getFilePath(newSessionData.pdf_url)" target="_blank" rel="noopener" class="pdf-attached-link">
                  <i class="bi bi-file-pdf"></i> {{ getPdfName(newSessionData.pdf_url) }}
                </a>
                <button type="button" @click="removePdf()" class="remove-file-button">Remove PDF</button>
              </div>
              <div class="form__item">
                <label class="form__label" for="weeklyDate">Select Day</label>
                <VueDatePicker :locale="'en-GB'" v-model="weeklyDate" name="weeklyDate" id="weeklyDate"
                  @update:model-value="setTheWeekNumber" :enable-time-picker="false" format="dd/MM/yyyy" />
              </div>
              <div class="form__item">
                <label class="form__label" for="sessionName">Drill Name <span class="uk-text-danger">*</span></label>
                <input :class="validationValues.name.state === true ? 'danger-outline' : ''"
                  @change="checkChangeVlaidation(newSessionData.name, 'name')" name="sessionName" class="form__input"
                  type="text" v-model="newSessionData.name" id="sessionName" />
                <p class="invalid-feedback" v-if="validationValues.name.state === true">{{ validationValues.name.text }}
                </p>
              </div>
              <div class="form__item">
                <label class="form__label" for="sessionIntroText">Drill description <span
                    class="uk-text-danger">*</span></label>
                <textarea :class="validationValues.introText.state === true ? 'danger-outline' : ''"
                  @change="checkChangeVlaidation(newSessionData.introText, 'introText')" id="sessionIntroText"
                  v-model="newSessionData.introText" placeholder="Add text" name="sessionIntroText"></textarea>
                <p class="invalid-feedback" v-if="validationValues.introText.state === true">{{
                  validationValues.introText.text }}</p>
              </div>

              <div class="form__item">
                <label class="form__label" for="sessionDetails">Drill details</label>
                <QuillEditor v-model:content="newSessionData.details" contentType="html" theme="snow" />
              </div>

              <div class="form__item">
                <label class="form__label" for="notes">Notes</label>
                <textarea id="notes" v-model="newSessionData.notes" placeholder="Add a note" name="notes"></textarea>
              </div>

              <!-- Exercise Linking Section -->
              <div class="form__item exercise-linking-section">
                <label class="form__label">
                  <i class="bi bi-journal-bookmark"></i> Linked Exercises
                </label>
                <div class="linked-exercises-preview">
                  <div v-if="selectedExercises.length === 0" class="no-exercises">
                    <p>No exercises linked to this programme yet.</p>
                  </div>
                  <div v-else class="selected-exercises-list">
                    <div v-for="exercise in selectedExercises" :key="exercise.id" class="selected-exercise-item">
                      <div class="exercise-info">
                        <span class="exercise-name">{{ exercise.exercise_name }}</span>
                        <span class="exercise-meta">
                          {{ formatCategory(exercise.category) }}
                          <span v-if="exercise.sets_reps"> - {{ exercise.sets_reps }}</span>
                        </span>
                      </div>
                      <button type="button" class="btn-remove-exercise" @click="removeExercise(exercise.id)"
                        title="Remove exercise">
                        <i class="bi bi-x"></i>
                      </button>
                    </div>
                  </div>
                  <button type="button" class="btn btn-outline-primary btn-add-exercises" @click="openExercisePicker">
                    <i class="bi bi-plus-lg"></i>
                    {{ selectedExercises.length > 0 ? 'Add More Exercises' : 'Add Exercises' }}
                  </button>
                </div>
              </div>

              <div class="form__item form-submit">
                <button type="submit" class="green-button">
                  {{ editMode === false ? 'Add drill' : 'Update drill' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Exercise Picker Modal -->
    <ExercisePicker :show="showExercisePicker" :initial-selected="selectedExerciseIds"
      @close="showExercisePicker = false" @select="handleExerciseSelection" />

    <!-- ── Programme Create/Edit Modal ───────────────────────────────────── -->
    <div v-if="showProgrammeModal" class="modal-overlay" @click.self="closeProgrammeModal">
      <div class="modal-dialog modal-lg add-edit-modal" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h3>{{ programmeEditMode ? 'Edit Programme' : 'Create Programme' }}</h3>
            <button @click="closeProgrammeModal" class="close-button">&times;</button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveProgrammeSubmit">
              <div class="form__item">
                <label class="form__label">Title <span class="uk-text-danger">*</span></label>
                <input class="form__input" v-model="newProgrammeData.title" placeholder="e.g. High Press Session"
                  required />
              </div>

              <div class="form__item">
                <label class="form__label">Description</label>
                <textarea v-model="newProgrammeData.description"
                  placeholder="What does this programme cover?"></textarea>
              </div>

              <div class="form__item">
                <label class="form__label">Category <span class="uk-text-danger">*</span></label>
                <select v-model="newProgrammeData.category" required>
                  <option value="">-- Select Category --</option>
                  <option v-for="cat in programmeCategories" :key="cat" :value="cat">{{ cat }}</option>
                </select>
              </div>

              <div class="form-classification-grid">
                <div class="form__item">
                  <label class="form__label">Area</label>
                  <select v-model="newProgrammeData.area">
                    <option value="">-- Select Area --</option>
                    <option v-for="a in programmeAreas" :key="a" :value="a">{{ a }}</option>
                  </select>
                </div>
                <div class="form__item">
                  <label class="form__label">No. of Players</label>
                  <select v-model="newProgrammeData.num_players">
                    <option value="">-- Select Players --</option>
                    <option v-for="p in programmePlayers" :key="p" :value="p">{{ p }}</option>
                  </select>
                </div>
                <div class="form__item">
                  <label class="form__label">Session Time</label>
                  <select v-model="newProgrammeData.session_time">
                    <option value="">-- Select Duration --</option>
                    <option v-for="t in programmeTimes" :key="t" :value="t">{{ t }}</option>
                  </select>
                </div>
              </div>

              <div class="form__item">
                <label class="form__label">Equipment</label>
                <div class="equipment-checkbox-group">
                  <label v-for="item in programmeEquipmentOptions" :key="item" class="equipment-checkbox-item">
                    <input type="checkbox" :value="item" v-model="newProgrammeData.equipment" />
                    {{ item }}
                  </label>
                </div>
              </div>

              <div class="form__item">
                <label class="form__label"><i class="bi bi-clipboard-check"></i> Drills in this programme</label>
                <div v-if="programmeLinkedDrills.length > 0" class="programme-drills-list">
                  <div v-for="(drill, index) in programmeLinkedDrills" :key="drill.id" class="programme-drill-item">
                    <span class="drill-order">{{ index + 1 }}</span>
                    <span class="drill-name">{{ drill.name }}</span>
                    <span v-if="drill.training_type" class="tag-chip tag-type"
                      :style="{ background: typeColor(drill.training_type) + '22', color: typeColor(drill.training_type) }">{{
                        drill.training_type }}</span>
                    <button type="button" class="btn-remove-exercise" @click="removeDrillFromProgramme(index)">
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                </div>
                <p v-else class="no-drills-msg">No drills added yet.</p>
                <button type="button" class="btn btn-outline-primary btn-add-exercises" @click="showDrillPicker = true">
                  <i class="bi bi-plus-lg"></i> Add Drills
                </button>
              </div>

              <div v-if="programmeEditMode && newProgrammeData.created_by_name" class="form__item">
                <p class="created-by-info"><i class="bi bi-person"></i> Created by {{
                  newProgrammeData.created_by_name }}</p>
              </div>

              <div class="form__item form-submit">
                <button type="submit" class="green-button">
                  {{ programmeEditMode
                    ? 'Update Programme'
                    : 'Create Programme'
                  }}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Drill Picker (for Programme modal) ────────────────────────────── -->
    <div v-if="showDrillPicker" class="modal-overlay" @click.self="showDrillPicker = false">
      <div class="modal-dialog modal-xl" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h3>Add Drills</h3>
            <button @click="showDrillPicker = false" class="close-button">&times;</button>
          </div>
          <div class="modal-body">
            <div class="search-box" style="margin-bottom:1rem; max-width:100%;">
              <i class="bi bi-search"></i>
              <input type="text" v-model="drillPickerSearch" placeholder="Search drills..." />
            </div>
            <div v-if="drillPickerFiltered.length > 0" class="training-plan-grid">
              <DrillCard v-for="drill in drillPickerFiltered" :key="drill.id" :drill="drill" :intro-length="80"
                class="drill-picker-card" :class="{ 'drill-already-added': isDrillInProgramme(drill.id) }" :badge="isDrillInProgramme(drill.id)
                  ? { label: 'Added', className: 'badge-added', icon: 'bi bi-check2' }
                  : null
                  " @open="toggleDrillInProgramme" />
            </div>
            <p v-else class="empty-state-text">No drills found.</p>
          </div>
          <div class="modal-footer" style="padding:1rem; border-top:1px solid #e2e8f0;">
            <button class="green-button" @click="showDrillPicker = false">Done</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Programme View Modal ───────────────────────────────────────────── -->
    <div v-if="viewingProgramme" class="modal-overlay" @click.self="viewingProgramme = null">
      <div class="modal-dialog modal-xl" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h3>{{ viewingProgramme.title }}</h3>
            <button @click="viewingProgramme = null" class="close-button">&times;</button>
          </div>
          <div class="modal-body">
            <div class="programme-meta-row" style="margin-bottom:0.75rem;">
              <span v-if="viewingProgramme.area" class="prog-badge"><i class="bi bi-geo-alt"></i> {{
                viewingProgramme.area }}</span>
              <span v-if="viewingProgramme.session_time" class="prog-badge"><i class="bi bi-clock"></i> {{
                viewingProgramme.session_time }}</span>
              <span v-if="viewingProgramme.num_players" class="prog-badge"><i class="bi bi-people"></i> {{
                viewingProgramme.num_players }}</span>
            </div>
            <div v-if="viewingProgramme.equipment && viewingProgramme.equipment.length" class="equipment-chips"
              style="margin-bottom:0.75rem;">
              <span v-for="item in viewingProgramme.equipment" :key="item" class="equipment-chip">{{ item }}</span>
            </div>
            <p v-if="viewingProgramme.description" class="programme-description" style="margin-bottom:1rem;">{{
              viewingProgramme.description }}</p>
            <p v-if="viewingProgramme.created_by_name" class="created-by-info" style="margin-bottom:1rem;">
              <i class="bi bi-person"></i> Created by {{ viewingProgramme.created_by_name }}
            </p>

            <div class="programme-drills-section">
              <h4 style="margin-bottom:1rem;"><i class="bi bi-clipboard-check"></i> Drills ({{
                (viewingProgramme.drills || []).length }})</h4>
              <div v-if="loadingProgrammeDrills" class="text-center py-3">Loading drills...</div>
              <div v-else-if="viewingProgramme.drills && viewingProgramme.drills.length > 0" class="training-plan-grid">
                <DrillCard v-for="drill in viewingProgramme.drills" :key="drill.id" :drill="drill" :intro-length="100"
                  @open="openTrainingInfoModel">
                  <template #meta>
                    <div class="card-meta-grid">
                      <span v-if="drill.section" class="meta-item"><i class="bi bi-tag"></i> {{ drill.section }}</span>
                      <span v-if="drill.category" class="meta-item"><i class="bi bi-folder"></i> {{ drill.category
                        }}</span>
                      <span v-if="drill.sub_category" class="meta-item"><i class="bi bi-folder2-open"></i> {{
                        drill.sub_category }}</span>
                      <span v-if="drill.training_type" class="meta-item"
                        :style="{ color: typeColor(drill.training_type) }"><i class="bi bi-lightning"></i> {{
                        drill.training_type }}</span>
                      <span v-if="drill.age_level" class="meta-item"><i class="bi bi-people"></i> {{ drill.age_level
                        }}</span>
                      <span v-if="drill.position" class="meta-item"><i class="bi bi-geo-alt"></i> {{ drill.position
                        }}</span>
                      <span v-if="drill.author_name" class="meta-item"><i class="bi bi-person"></i> {{ drill.author_name
                        }}</span>
                    </div>
                  </template>
                </DrillCard>
              </div>
              <p v-else class="empty-state-text">No drills linked to this programme yet.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters } from "vuex";
import heic2any from "heic2any";
import { TrainingPlan } from "@/services/training-plan-service.js";
import { TrainingProgramme } from "@/services/training-programme-service.js";
import { Comments } from "@/services/comments-service.js";
import { Teams } from "@/services/teams-service.js";
import { Users } from "@/services/user-service.js";
import SortingByCreatedDate from "../../components/Sorting";
import CommentsComp from "../../components/Comments";
import SpinnerLoader from "../../components/LoadingSpinning";
import ExercisePicker from "../../components/ExercisePicker";
import moment from "moment";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import TacticalBoardPicker from "../TacticalBoard/TacticalBoardPicker.vue";
import TacticalBoardCanvas from "../TacticalBoard/TacticalBoardCanvas.vue";
import TacticalBoard from "../TacticalBoard/index.vue";
import DrillCard from "../DrillCard/index.vue";
import { TacticalBoardService } from "@/services/tactical-board-service.js";
import { ReturnToPlayService } from "@/services/rtp-service.js";
import {
  TRAINING_SECTIONS,
  TRAINING_SECTION_LIST,
} from "@/constants/training-sections.js";

export default {
  name: "TrainingPlanLibrary",
  components: {
    VueDatePicker,
    SortingByCreatedDate,
    SpinnerLoader,
    CommentsComp,
    QuillEditor,
    ExercisePicker,
    TacticalBoardPicker,
    TacticalBoardCanvas,
    TacticalBoard,
    DrillCard,
  },
  data() {
    return {
      loadingStuff: false,
      showSessionModal: false,
      showAddEditProgramme: false,
      filterSelectedValue: [...TRAINING_SECTION_LIST],
      sectionOptions: TRAINING_SECTION_LIST,
      toast: { show: false, message: "", type: "success" },
      toastTimer: null,
      searchDebounceTimer: null,
      programmeSearchQuery: "",
      drillsPage: 1,
      drillsPageSize: 24,
      drillsTotal: 0,
      loadingMoreDrills: false,
      imageFile: null,
      imageToBeRemoved: null,
      imageURL: "",
      editMode: false,
      image: {
        imageName: "",
        path: "",
        file: null,
      },
      pdfUploading: false,
      trainingPlanData: [],
      filteredTrainingPlanData: [],
      searchQuery: "",
      coachesUsersData: [],
      sessionDataModal: {},
      needsValidation: [],
      validationMessage: null,
      validationValues: {
        name: {
          state: false,
          text: "Please add a name",
        },
        introText: {
          state: false,
          text: "Please add session intro Text",
        },
      },
      weeklyDate: "",
      selectedSessionTab: "details",
      linkedTacticalBoards: [],
      cardBoards: {},
      showTacticalBoardPicker: false,
      loadingTacticalBoards: false,
      viewingTacticalBoard: null,
      newSessionData: {
        userId: "",
        section: "",
        details: "",
        weekSelectedNumber: "",
        name: "",
        introText: "",
        notes: "",
        day: "",
        year: "",
        week: "",
        category: "",
        sub_category: "",
        training_type: "",
        age_level: "",
        position: "",
        pdf_url: "",
      },
      listOfSelectedUserIds: [],
      listOfSelectedUserIdsToRemove: [],
      commentsData: [],
      // Exercise picker data
      showExercisePicker: false,
      selectedExerciseIds: [],
      selectedExercises: [],
      // Team assignment
      assignmentMode: "individual",
      selectedTeamIds: [],
      selectedTeamIdsToRemove: [],
      availableTeams: [],
      // Category filters
      showFilters: false,
      filterCategory: '',
      filterSubCategory: '',
      filterType: '',
      filterLevel: '',
      filterPosition: '',
      categoryTree: {
        'Attacking': ['Attacking transition game', 'Counter attack', 'Create the attack', 'Finish the attack', 'Penetration', 'Combination play', 'Inventive play', 'Wide play', 'Switching play'],
        'Defending': ['Counter/Reactive pressure', 'High-block and press', 'Low-block and press', 'Mid-block and press', 'Fight zone'],
        'Build-up Play': ['Playing through the thirds', 'Start the attack', 'Playing out from the back'],
        'Positional': ['Defender', 'Midfielder', 'Striker', 'Position specific', 'Positional understanding'],
        'Goalkeeper': ['1v1', 'Agility/Diving techniques', 'Crossing/High balls', 'Distribution', 'Footwork/Handling', 'Reaction saves', 'Shot stopping'],
        'Physical': ['Agility', 'Endurance', 'Speed', 'Strength/Power'],
        'Set Pieces': ['Corners', 'Free-kicks', 'Throw-ins'],
        'Game Forms': ['Small-Sided Games', 'Full game form', 'Functional'],
        'Technical Skills': ['Ball Control', 'Bilateral ball striking', 'Coerver/Individual Skills', 'Crossing & Finishing', 'Dribbling and RWB', 'Dragging', 'Heading', 'Movement off the ball', 'Passing & Receiving', 'Shooting', 'Turning', 'Weak foot work', 'Attacking skills', 'Defensive skills'],
        'Tactical Concepts': ['Attacking principles', 'Defensive principles', 'Decision making practices', 'Possession'],
        'Warm-ups': ['General warm-ups'],
        'Other': ['Psychological practices', 'Referees'],
        'Rehabilitation': ['Injury prevention', 'Rehabilitation exercises'],
      },
      trainingTypes: ['Technical', 'Tactical', 'Physical', 'Mental'],
      ageLevels: ['Academy U10', 'Academy U11', 'Academy U12', 'Academy U13', 'Academy U14', 'Academy U15', 'Academy U17', 'Academy U19', 'Mixed age'],
      positionOptions: ['Goalkeeper', 'Right Full-back (Wingback)', 'Left Full-back (Wingback)', 'Center-back left', 'Center-back right', 'Defensive Midfielder', 'Centre midfield', 'Centre Defensive Midfield', 'Right Midfielder (Winger)', 'Left Midfielder (Winger)', 'Attacking Midfielder', 'Center Forward (Striker) left', 'Center Forward (Striker) right', 'all positions'],
      // ── Programme feature ─────────────────────────────────────────────────
      libraryTab: 'programmes',
      programmes: [],
      showProgrammeModal: false,
      programmeEditMode: false,
      newProgrammeData: {
        title: '',
        description: '',
        area: '',
        equipment: [],
        num_players: '',
        session_time: '',
        section: '',
        category: '',
      },
      programmeLinkedDrills: [],
      showDrillPicker: false,
      drillPickerSearch: '',
      viewingProgramme: null,
      loadingProgrammeDrills: false,
      programmeFilterCategory: '',
      programmeCategories: ['Build Phase', 'Control Phase', 'Create Phase', 'CV Sessions', 'Games Phase', "GK's", 'IDP Sessions', 'Pre Academy', 'Single Activities', 'Sports Science Sessions'],
      programmeAreas: ['Full pitch', 'Half pitch', 'Third of pitch', 'Quarter of pitch', 'Small box area', 'Penalty area'],
      programmePlayers: ['4–6 players', '7–9 players', '10–14 players', '15–18 players', '18 + 3 goalkeepers', 'Full squad (22+)'],
      programmeTimes: ['20 mins', '30 mins', '45 mins', '60 mins', '75 mins', '90 mins'],
      programmeEquipmentOptions: ['Balls', 'Bibs', 'Mannequins', 'Flat markers', 'Mini goals', 'Full size goals', 'Cones', 'Training poles', 'Agility ladder'],
    };
  },
  props: {
    trainingTypeAndUserData: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters(["getProfile", "getCoachesUsersData"]),
    drillPickerFiltered() {
      const q = (this.drillPickerSearch || '').toLowerCase();
      if (!q) return this.trainingPlanData;
      return this.trainingPlanData.filter(
        d => (d.name && d.name.toLowerCase().includes(q)) || (d.introText && d.introText.toLowerCase().includes(q))
      );
    },
    filteredProgrammes() {
      let list = this.programmes;
      if (this.programmeFilterCategory) {
        list = list.filter(p => p.category === this.programmeFilterCategory);
      }
      const q = this.programmeSearchQuery.trim().toLowerCase();
      if (q) {
        list = list.filter(
          p =>
            (p.title && p.title.toLowerCase().includes(q)) ||
            (p.description && p.description.toLowerCase().includes(q))
        );
      }
      return list;
    },
  },
  watch: {
    trainingTypeAndUserData: function () {
      this.getTrainingPlansData();
    },
    getProfile: {
      handler(newProfile) {
        if (newProfile && newProfile.clubId && (!this.userData || !this.userData.clubId)) {
          this.userData = newProfile;
          this.loadCoachesUsers();
          this.loadAvailableTeams();
          this.loadProgrammes();
        }
      },
      immediate: false,
    },
  },
  created() {
    this.userData = this.getProfile;
    this.getTrainingPlansData();
    if (this.userData && this.userData.clubId) {
      this.loadCoachesUsers();
      this.loadAvailableTeams();
      this.loadProgrammes();
    }
  },
  beforeUnmount() {
    clearTimeout(this.toastTimer);
    clearTimeout(this.searchDebounceTimer);
  },
  methods: {
    showToast(message, type = "success") {
      clearTimeout(this.toastTimer);
      this.toast = { show: true, message, type };
      this.toastTimer = setTimeout(() => {
        this.toast = { ...this.toast, show: false };
      }, 3000);
    },
    // Search & Filter
    onSearchInput() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.filterTrainingPlans();
      }, 250);
    },
    filterTrainingPlans() {
      // Library mode: search/filters run in SQL - refetch page 1
      if (this.trainingTypeAndUserData?.trainingData === "library") {
        this.getTrainingPlanLibraryData(
          this.trainingTypeAndUserData.trainingType,
        );
        return;
      }

      // Assigned-plans mode still filters the loaded set client-side
      let filtered = this.trainingPlanData;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (plan) =>
            (plan.name && plan.name.toLowerCase().includes(query)) ||
            (plan.introText && plan.introText.toLowerCase().includes(query)) ||
            (plan.section && plan.section.toLowerCase().includes(query)),
        );
      }

      if (this.filterCategory) {
        filtered = filtered.filter(p => p.category === this.filterCategory);
      }
      if (this.filterSubCategory) {
        filtered = filtered.filter(p => p.sub_category === this.filterSubCategory);
      }
      if (this.filterType) {
        filtered = filtered.filter(p => p.training_type === this.filterType);
      }
      if (this.filterLevel) {
        filtered = filtered.filter(p => p.age_level === this.filterLevel);
      }
      if (this.filterPosition) {
        filtered = filtered.filter(p => p.position === this.filterPosition);
      }

      this.filteredTrainingPlanData = filtered;
    },
    clearSearch() {
      this.searchQuery = "";
      this.filterTrainingPlans();
    },
    clearAllFilters() {
      this.searchQuery = "";
      this.filterCategory = "";
      this.filterSubCategory = "";
      this.filterType = "";
      this.filterLevel = "";
      this.filterPosition = "";
      this.filterTrainingPlans();
    },
    hasActiveFilters() {
      return this.filterCategory || this.filterSubCategory || this.filterType || this.filterLevel || this.filterPosition;
    },
    availableSubCategories() {
      if (!this.filterCategory || !this.categoryTree[this.filterCategory]) return [];
      return this.categoryTree[this.filterCategory];
    },
    formSubCategories() {
      if (!this.newSessionData.category || !this.categoryTree[this.newSessionData.category]) return [];
      return this.categoryTree[this.newSessionData.category];
    },
    onFilterCategoryChange() {
      this.filterSubCategory = "";
      this.filterTrainingPlans();
    },
    onFormCategoryChange() {
      this.newSessionData.sub_category = "";
    },
    typeColor(type) {
      const colors = { 'Technical': '#3b82f6', 'Tactical': '#8b5cf6', 'Physical': '#f97316', 'Mental': '#10b981' };
      return colors[type] || '#64748b';
    },

    // UI helpers
    truncateText(text, maxLength) {
      if (!text) return "";
      return text.length > maxLength
        ? text.substring(0, maxLength) + "..."
        : text;
    },

    // Modal management
    closeAddEditModal() {
      this.showAddEditProgramme = false;
      this.resetUserIdList();
    },

    // User assignment
    resetUserIdList() {
      this.listOfSelectedUserIds = [];
      this.listOfSelectedUserIdsToRemove = [];
      this.selectedTeamIds = [];
      this.selectedTeamIdsToRemove = [];
    },
    addTolistOfSelectedUserIds(id) {
      this.listOfSelectedUserIds.push(id);
      this.listOfSelectedUserIdsToRemove =
        this.listOfSelectedUserIdsToRemove.filter((item) => item !== id);
    },
    removeFromlistOfSelectedUserIds(id) {
      this.listOfSelectedUserIdsToRemove.push(id);
      this.listOfSelectedUserIds = this.listOfSelectedUserIds.filter(
        (item) => item !== id,
      );
    },
    userIdsAddRemove(data) {
      data.target.checked === true
        ? this.addTolistOfSelectedUserIds(data.target.id)
        : this.removeFromlistOfSelectedUserIds(data.target.id);
    },
    selectAllUsers() {
      this.coachesUsersData.forEach((user) => {
        user.selected = true;
        if (!this.listOfSelectedUserIds.includes(user.id)) {
          this.listOfSelectedUserIds.push(user.id);
        }
      });
      this.listOfSelectedUserIdsToRemove = [];
    },
    deselectAllUsers() {
      this.coachesUsersData.forEach((user) => {
        user.selected = false;
        if (!this.listOfSelectedUserIdsToRemove.includes(user.id)) {
          this.listOfSelectedUserIdsToRemove.push(user.id);
        }
      });
      this.listOfSelectedUserIds = [];
    },

    // Load players for assignment
    async loadCoachesUsers() {
      try {
        const clubId = this.userData?.clubId || this.getProfile?.clubId;
        if (!clubId) return;
        const response = await Users.find({
          action: "getUserByClubId",
          clubId: clubId,
        });
        if (response.data && response.data.length > 0) {
          this.coachesUsersData = response.data;
        }
      } catch (error) {
        console.error("Error loading users:", error);
      }
    },

    // Team assignment
    async loadAvailableTeams() {
      try {
        const clubId = this.userData?.clubId || this.getProfile?.clubId;
        if (!clubId) return;
        const response = await Teams.find({
          action: "populateTeamData",
          clubId: clubId,
        });
        if (response.data && Array.isArray(response.data)) {
          this.availableTeams = response.data;
        }
      } catch (error) {
        console.error("Error loading teams:", error);
      }
    },

    // Image handling
    imageChange(event, imageFileName, imageNumber) {
      const file = event.currentTarget.files[0];
      this.createObjectURLCheck(file, imageFileName, imageNumber);
      this.image.file = file;
      this.image.imageName = file.name;

      var files = event.target.files || event.dataTransfer.files;
      if (!files.length) return;
      this.createImage(files[0]);
    },
    createImage(file) {
      var reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        this.imageFile = event.target.result;
      };
    },
    createObjectURLCheck(file, imageFileName, imageNumber) {
      if (file.type === "image/heic") {
        this.convertHeicToJpg(file, imageFileName, imageNumber);
      }
      "srcObject" in this
        ? (this.srcObject = file)
        : (this.image.path = URL.createObjectURL(file));
    },
    async convertHeicToJpg(file, imageFileName, imageNumber) {
      let base64 = URL.createObjectURL(file);
      fetch(base64)
        .then((res) => res.blob())
        .then((blob) => heic2any({ blob, toType: "image/jpeg" }))
        .then((conversionResult) => {
          let base64 = URL.createObjectURL(conversionResult);
          this[imageNumber].path = base64;
          this[imageFileName] = new File([conversionResult], "newimage", {
            type: "image/jpeg",
          });
        })
        .catch((error) => {
          console.log(error);
        });
    },
    removeFileFromUI() {
      this.imageURL = "";
      this.image = { imageName: "", path: "", file: null };
    },

    // PDF attachment — uploaded immediately via the shared exercise-file
    // endpoint; only the returned web-relative path is saved with the drill.
    async pdfChange(event) {
      const file = event.currentTarget.files[0];
      if (!file) return;
      if (file.type !== "application/pdf") {
        this.showToast("Please choose a PDF file", "error");
        event.target.value = "";
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        this.showToast("PDF too large (max 20MB)", "error");
        event.target.value = "";
        return;
      }
      this.pdfUploading = true;
      try {
        const clubId = this.userData?.clubId || this.getProfile?.clubId || null;
        const base64 = await this.fileToBase64(file);
        const response = await ReturnToPlayService.uploadExerciseFile(
          clubId,
          file.name,
          base64,
          "pdf",
        );
        if (response.data && response.data.success) {
          this.newSessionData.pdf_url = response.data.file_path;
          this.showToast("PDF uploaded");
        } else {
          this.showToast(
            (response.data && response.data.message) || "Failed to upload PDF",
            "error",
          );
        }
      } catch (error) {
        console.error("Error uploading PDF:", error);
        this.showToast("Error uploading PDF", "error");
      } finally {
        this.pdfUploading = false;
        if (this.$refs.drillPdf) this.$refs.drillPdf.value = "";
      }
    },
    removePdf() {
      this.newSessionData.pdf_url = "";
      if (this.$refs.drillPdf) this.$refs.drillPdf.value = "";
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    getFilePath(path) {
      if (!path) return "";
      if (path.startsWith("http")) return path;
      return "/" + path.replace(/^\//, "");
    },
    getPdfName(path) {
      if (!path) return "PDF";
      const filename = path.split("/").pop();
      const idx = filename.indexOf("_");
      return idx > 0 && idx < 20 ? filename.substring(idx + 1) : filename;
    },

    // Sorting (re-orders the loaded set client-side; must not refetch)
    sortedData(sortedDataArray) {
      this.trainingPlanData = sortedDataArray;
      this.filteredTrainingPlanData = sortedDataArray;
    },

    // Edit modal setup
    showEditAddProgrammeModal(type, session) {
      this.image.path = "";
      this.editMode = false;
      this.selectedExerciseIds = [];
      this.selectedExercises = [];
      this.assignmentMode = "individual";
      this.selectedTeamIds = [];

      if (type === "edit") {
        this.editMode = true;
        this.newSessionData = { ...session };

        if (session.imageURL !== "") {
          this.image.path = this.getImgUrl(session.imageURL);
        }

        this.gettrainingPlanByTrainingPlanId(session.id);
        this.loadExercisesForPlan(session.id);
      } else {
        this.newSessionObjectReset();
        let today = moment();
        this.weeklyDate = today._d;
        this.setTheWeekNumber(this.weeklyDate);
        this.newSessionData.userId = this.selectedUserValue;
      }
      this.showAddEditProgramme = true;

      // Always load players and teams when modal opens
      this.loadCoachesUsers();
      this.loadAvailableTeams();
    },

    async loadExercisesForPlan(planId) {
      try {
        const data = { id: planId, action: "getTrainingPlanById" };
        const response = await TrainingPlan.find(data).catch((error) => {
          console.log(error);
        });

        if (response.data && response.data.exercises) {
          this.selectedExercises = response.data.exercises;
          this.selectedExerciseIds = response.data.exercise_ids || [];
        }
      } catch (error) {
        console.error("Error loading exercises for plan:", error);
      }
    },

    // URL helpers
    checkforDotAtTheStartOfUrl(p) {
      return p.startsWith(".") ? p.substring(1) : p;
    },
    checkTheimageForBlob(p, imageNumber) {
      if (p !== null) {
        var URL = this.checkforDotAtTheStartOfUrl(p);
      }
      switch (typeof URL) {
        case "string":
          URL.search("blob:") !== -1
            ? p
            : `${window.location.protocol}//${window.location.host}${URL}`;
          return URL.search("blob:") !== -1
            ? p
            : `${window.location.protocol}//${window.location.host}${URL}`;
        case "object":
          return this[imageNumber].path;
      }
    },
    getPath(p, imageNumber) {
      if (p === undefined) return;
      return this.checkTheimageForBlob(p, imageNumber);
    },
    getImgUrl(pic) {
      return pic !== undefined && pic !== "" ? `/images/${pic}` : "";
    },
    tabChange(tabValue, selectedTabValue) {
      this[tabValue] = selectedTabValue;
    },

    // Tactical boards
    async loadTacticalBoardsForPlan() {
      if (!this.sessionDataModal.id) return;
      this.loadingTacticalBoards = true;
      try {
        const res = await TacticalBoardService.getBoardsForTrainingPlan(
          this.sessionDataModal.id,
        );
        if (res.data.success) {
          this.linkedTacticalBoards = res.data.data || [];
        }
      } catch (err) {
        console.error("Error loading tactical boards:", err);
      } finally {
        this.loadingTacticalBoards = false;
      }
    },
    async onTacticalBoardsSelected(boards) {
      for (const board of boards) {
        try {
          await TacticalBoardService.linkBoardToTrainingPlan({
            board_id: board.id,
            training_plan_id: this.sessionDataModal.id,
            sort_order: this.linkedTacticalBoards.length,
          });
        } catch (err) {
          console.error("Error linking board:", err);
        }
      }
      this.loadTacticalBoardsForPlan();
    },
    async unlinkTacticalBoard(boardId, idx) {
      if (!confirm("Remove this tactical board from the training plan?"))
        return;
      try {
        await TacticalBoardService.unlinkBoardFromTrainingPlan(
          boardId,
          this.sessionDataModal.id,
        );
        this.linkedTacticalBoards.splice(idx, 1);
      } catch (err) {
        console.error("Error unlinking board:", err);
      }
    },
    onTacticalBoardSaved(savedBoard) {
      const idx = this.linkedTacticalBoards.findIndex(b => b.id === savedBoard.id);
      if (idx !== -1) {
        this.linkedTacticalBoards.splice(idx, 1, savedBoard);
      }
      this.viewingTacticalBoard = savedBoard;
    },
    async onTacticalBoardDeleted(board) {
      try {
        const res = await TacticalBoardService.deleteTacticalBoard(board.id);
        if (res.data.success) {
          this.linkedTacticalBoards = this.linkedTacticalBoards.filter(b => b.id !== board.id);
          this.viewingTacticalBoard = null;
        }
      } catch (err) {
        console.error("Error deleting tactical board:", err);
      }
    },

    // Session detail modal
    async openTrainingInfoModel(sessionData) {
      // Reset stale state from previously viewed drill
      this.linkedTacticalBoards = [];
      this.selectedSessionTab = "details";
      this.commentsData = [];
      this.viewingTacticalBoard = null;
      this.showTacticalBoardPicker = false;

      this.sessionDataModal = { ...sessionData };
      this.getComments(sessionData.id);

      try {
        const data = { id: sessionData.id, action: "getTrainingPlanById" };
        const response = await TrainingPlan.find(data);
        if (response.data && response.data.exercises) {
          this.sessionDataModal.exercises = response.data.exercises;
        }
      } catch (error) {
        console.error("Error loading exercises:", error);
      }

      this.showSessionModal = true;
      this.loadTacticalBoardsForPlan();
    },

    // Data fetching
    getTheUserId() {
      if (
        this.userData.userType === "Admin" ||
        this.userData.userType === "Coach"
      ) {
        return this.trainingTypeAndUserData.userId;
      } else {
        return this.userData.id;
      }
    },
    getTrainingPlansByUserIdAndWeekSetUpCall(traningType) {
      if (this.getTheUserId() !== undefined) {
        this.gettrainingPlansByUserIdAndWeek(this.getTheUserId(), traningType);
      }
    },
    async getTrainingPlansData() {
      this.trainingTypeAndUserData.trainingData === "library"
        ? this.getTrainingPlanLibraryData(
          this.trainingTypeAndUserData.trainingType,
        )
        : this.getTrainingPlansByUserIdAndWeekSetUpCall(
          this.trainingTypeAndUserData.trainingType,
        );
    },
    setSelectedCheckDefaults() {
      if (!this.coachesUsersData || !Array.isArray(this.coachesUsersData)) return;
      let arrayWithSelectedDefaults = this.coachesUsersData.map((user) => {
        return { ...user, selected: false };
      });
      this.coachesUsersData = arrayWithSelectedDefaults;
    },
    addCheckedValuetoUsers(ids) {
      if (!this.coachesUsersData || !Array.isArray(this.coachesUsersData)) return;
      this.setSelectedCheckDefaults();
      let arrayWithSelectedTrue;
      ids.map((id) => {
        arrayWithSelectedTrue = this.coachesUsersData.map((user) => {
          if (user.id == id.user_id) {
            user.selected = true;
          }
          return user;
        });
      });
      this.coachesUsersData = arrayWithSelectedTrue;
    },
    async gettrainingPlanByTrainingPlanId(trainingPalnId) {
      const data = {
        trainingPalnId: trainingPalnId,
        action: "gettrainingPlanByTrainingPlanId",
      };
      const responce = await TrainingPlan.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.addCheckedValuetoUsers(responce.data);
      }
    },
    async getTrainingPlanLibraryData(trainingType, append = false) {
      const page = append ? this.drillsPage + 1 : 1;
      const trainingPlanData = {
        trainingType: trainingType,
        clubId: this.userData.clubId,
        action: "gettrainingPlanByClubId",
        page: page,
        page_size: this.drillsPageSize,
        search: this.searchQuery || "",
        category: this.filterCategory || "",
        sub_category: this.filterSubCategory || "",
        training_type: this.filterType || "",
        age_level: this.filterLevel || "",
        position: this.filterPosition || "",
      };

      if (append) this.loadingMoreDrills = true;
      try {
        const responce = await TrainingPlan.find(trainingPlanData);
        const payload = responce.data || {};
        const rows = Array.isArray(payload.data) ? payload.data : [];
        this.drillsPage = payload.page || page;
        this.drillsTotal = payload.total ?? rows.length;
        this.trainingPlanData = append
          ? [...this.trainingPlanData, ...rows]
          : rows;
        this.filteredTrainingPlanData = this.trainingPlanData;
        this.loadCardBoards();
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingMoreDrills = false;
      }
    },
    loadMoreDrills() {
      this.getTrainingPlanLibraryData(
        this.trainingTypeAndUserData?.trainingType || TRAINING_SECTIONS.TRAINING,
        true,
      );
    },
    async gettrainingPlansByUserIdAndWeek(clientId, trainingType) {
      this.loadingStuff = false;
      const trainingPlanData = {
        userId: clientId,
        trainingType: trainingType,
        week: moment().isoWeek(),
        action: "getTrainingPlanByUserIdAndWeek",
      };
      const responce = await TrainingPlan.find(trainingPlanData).catch(
        (error) => {
          console.log(error);
        },
      );

      this.trainingPlanData =
        responce.data.length > 0 ? responce.data : [];
      this.filteredTrainingPlanData = this.trainingPlanData;
      this.loadCardBoards();
      this.loadingStuff = false;
    },
    async loadCardBoards() {
      const planIds = this.trainingPlanData
        .filter((session) => !session.imageURL)
        .map((session) => session.id);
      if (planIds.length === 0) {
        this.cardBoards = {};
        return;
      }
      try {
        const res =
          await TacticalBoardService.getFirstBoardForTrainingPlans(planIds);
        if (res.data.success) {
          this.cardBoards = res.data.data || {};
        }
      } catch (err) {
        console.error("Error loading card boards:", err);
      }
    },
    newSessionObjectReset() {
      this.weeklyDate = "";
      this.newSessionData = {
        userId: "",
        section: this.trainingTypeAndUserData?.trainingType || "Training Plan",
        details: "",
        weekSelectedNumber: "",
        name: "",
        introText: "",
        notes: "",
        day: "",
        year: "",
        week: "",
        clubId: this.userData.clubId,
        category: "",
        sub_category: "",
        training_type: "",
        age_level: "",
        position: "",
        pdf_url: "",
      };
      this.selectedExerciseIds = [];
      this.selectedExercises = [];
    },
    setTheWeekNumber(date) {
      this.weekSelectedNumber = moment(date).isoWeek();
      this.newSessionData.week = moment(date).isoWeek();
      this.newSessionData.day = moment(date, "DD MMM YYYY").day();
      this.newSessionData.year = moment(date, "DD MMM YYYY").year();
    },
    async deletetrainingPlan(id, imageURL) {
      if (!confirm("Are you sure you want to delete this drill?")) return;
      const data = {
        id: id,
        imageURL: imageURL,
        clubId: this.userData.clubId,
        action: "deletetrainingPlan",
      };
      try {
        await TrainingPlan.find(data);
        this.showToast('Drill deleted');
      } catch (error) {
        console.error(error);
        this.showToast('Error deleting drill', 'error');
      }
      this.getTrainingPlansData();
    },
    async duplicatePlan(planId) {
      try {
        this.loadingStuff = true;
        const response = await TrainingPlan.duplicate(planId);
        if (response.data && response.data.success) {
          this.getTrainingPlansData();
        }
      } catch (error) {
        console.error("Error duplicating plan:", error);
      } finally {
        this.loadingStuff = false;
      }
    },

    // Validation
    checkChangeVlaidation(value, nameOfElementToValidate) {
      if (value.length > 0) {
        this.validationValues[nameOfElementToValidate].state = false;
      }
    },
    setValidationMessage() {
      if (this.needsValidation.length === 0) {
        this.validationMessage = null;
        this.needsValidation = [];
      } else {
        this.validationMessage =
          "Please fix the validation errors before you can proceed.";
        this.isLoading = false;
      }
    },
    reCheckValidation() {
      this.setValidationMessage();
      return this.needsValidation.length === 0;
    },
    populateValidationArray(validationData) {
      return validationData.map((validation) => {
        return validation === true;
      });
    },
    filterValidationResults(validationResults) {
      return validationResults.filter((result) => {
        return result === true;
      });
    },
    togglevalaidationErrors(name, validationState) {
      this.validationValues[name].state = validationState;
    },
    requiredValidation(value) {
      return !value || value.length === 0;
    },
    setUpValidation(validationValue, validationName) {
      const validationstate = this.requiredValidation(validationValue);
      this.togglevalaidationErrors(validationName, validationstate);
      return validationstate;
    },
    validationCheck() {
      let validationData = [];
      this.needsValidation = [];
      validationData.push(
        this.setUpValidation(this.newSessionData.name, "name"),
      );
      validationData.push(
        this.setUpValidation(this.newSessionData.introText, "introText"),
      );
      let validationResults = this.populateValidationArray(validationData);
      this.needsValidation = this.filterValidationResults(validationResults);
      return this.reCheckValidation();
    },

    // CRUD
    addEditProgrammeSubmit(e) {
      if (this.editMode === false) {
        if (this.validationCheck(e) === true) {
          this.savetrainingNewPlan();
        }
      } else {
        if (this.validationCheck(e) === true) {
          this.edittrainingPlan();
        }
      }
    },
    async savetrainingNewPlan() {
      this.loadingStuff = true;
      const trainingPlanData = this.makeTraingDataObject(
        "savetrainingPlanToDd",
      );
      const responce = await TrainingPlan.store(trainingPlanData).catch(
        (error) => {
          console.log(error);
        },
      );
      if (responce.data === "Training Plan added.") {
        this.actionsAfterFitnessSessionEditCreate(
          "Created",
          "Created successfully.",
        );
      }
    },
    makeTraingDataObject(actionName) {
      let data = this.newSessionData;
      data.complete = false;
      data.action = actionName;
      data.listOfSelectedUserIds = this.listOfSelectedUserIds;
      data.listOfSelectedUserIdsToRemove = this.listOfSelectedUserIdsToRemove;
      data.exercise_ids = this.selectedExerciseIds;
      data.listOfSelectedTeamIds = this.selectedTeamIds;
      data.listOfSelectedTeamIdsToRemove = this.selectedTeamIdsToRemove;

      if (this.image.file !== null) {
        data.imageFile = this.imageFile;
        data.fileName = this.image.imageName;
      }
      return data;
    },
    actionsAfterFitnessSessionEditCreate(toastTitle, toastMessage) {
      this.showAddEditProgramme = false;
      this.getTrainingPlansData();
      this.loadingStuff = false;
      this.showToast(`${toastTitle}: drill ${toastMessage}`);
    },
    async edittrainingPlan() {
      this.loadingStuff = true;
      const trainingPlanData = this.makeTraingDataObject("edittrainingPlan");
      const responce = await TrainingPlan.store(trainingPlanData).catch(
        (error) => {
          console.log(error);
        },
      );

      if (responce.data === "Training session updated") {
        this.actionsAfterFitnessSessionEditCreate(
          "Updated",
          "updated successfully.",
        );
        this.editMode = false;
      }
    },
    async getComments(type_id) {
      this.commentsData = [];
      const data = { type_id: type_id, teamId: 0, action: "getComments" };
      const responce = await Comments.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.commentsData = responce.data;
      }
    },

    // Exercise linking
    openExercisePicker() {
      this.showExercisePicker = true;
    },
    handleExerciseSelection(selection) {
      this.selectedExerciseIds = selection.ids;
      this.selectedExercises = selection.exercises;
    },
    removeExercise(exerciseId) {
      this.selectedExerciseIds = this.selectedExerciseIds.filter(
        (id) => id !== parseInt(exerciseId),
      );
      this.selectedExercises = this.selectedExercises.filter(
        (ex) => parseInt(ex.id) !== parseInt(exerciseId),
      );
    },
    formatCategory(category) {
      if (!category) return "";
      return category
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
    },
    clearExerciseSelection() {
      this.selectedExerciseIds = [];
      this.selectedExercises = [];
    },

    // ── Programme methods ──────────────────────────────────────────────────//
    switchLibraryTab(tab) {
      this.libraryTab = tab;
      // Only fetch when the tab has no data yet - avoids a refetch on
      // every switch
      if (tab === 'programmes' && this.programmes.length === 0) {
        this.loadProgrammes();
      } else if (tab === 'drills' && this.trainingPlanData.length === 0) {
        this.getTrainingPlansData();
      }
    },
    async loadProgrammes() {
      if (!this.userData || !this.userData.clubId) return;
      try {
        const section = this.trainingTypeAndUserData?.trainingType || 'Training Plan';
        const res = await TrainingProgramme.findByClub(this.userData.clubId, section);
        this.programmes = Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        console.error('Error loading programmes:', err);
      }
    },
    openProgrammeModal(type, programme) {
      this.programmeEditMode = type === 'edit';
      this.programmeLinkedDrills = [];
      if (type === 'edit' && programme) {
        this.newProgrammeData = {
          id: programme.id,
          title: programme.title || '',
          description: programme.description || '',
          area: programme.area || '',
          equipment: Array.isArray(programme.equipment) ? [...programme.equipment] : [],
          num_players: programme.num_players || '',
          session_time: programme.session_time || '',
          section: programme.section || this.trainingTypeAndUserData?.trainingType || 'Training Plan',
          category: programme.category || '',
          created_by_name: programme.created_by_name || '',
        };
        // Load full drills for the programme
        this.loadProgrammeForEdit(programme.id);
      } else {
        this.newProgrammeData = { title: '', description: '', area: '', equipment: [], num_players: '', session_time: '', section: this.trainingTypeAndUserData?.trainingType || 'Training Plan', category: '' };
      }
      this.showProgrammeModal = true;
    },
    async loadProgrammeForEdit(id) {
      try {
        const res = await TrainingProgramme.show(id);
        if (res.data && res.data.drills) {
          this.programmeLinkedDrills = res.data.drills;
        }
      } catch (err) {
        console.error('Error loading programme drills:', err);
      }
    },
    closeProgrammeModal() {
      this.showProgrammeModal = false;
      this.programmeLinkedDrills = [];
      this.drillPickerSearch = '';
    },
    async saveProgrammeSubmit() {
      if (!this.newProgrammeData.title.trim()) return;
      try {
        const payload = {
          ...this.newProgrammeData,
          clubId: this.userData.clubId,
          drill_ids: this.programmeLinkedDrills.map(d => d.id),
        };
        if (this.programmeEditMode) {
          payload.edited_by = this.userData.id;
          await TrainingProgramme.update(payload);
        } else {
          payload.created_by = this.userData.id;
          await TrainingProgramme.store(payload);
        }
        this.closeProgrammeModal();
        this.loadProgrammes();
        this.showToast(this.programmeEditMode ? 'Programme updated' : 'Programme created');
      } catch (err) {
        console.error('Error saving programme:', err);
        this.showToast('Error saving programme', 'error');
      }
    },
    async deleteProgramme(id) {
      if (!confirm('Are you sure you want to delete this programme?')) return;
      try {
        await TrainingProgramme.delete(id, this.userData.clubId);
        this.loadProgrammes();
        this.showToast('Programme deleted');
      } catch (err) {
        console.error('Error deleting programme:', err);
        this.showToast('Error deleting programme', 'error');
      }
    },
    async duplicateProgramme(id) {
      try {
        await TrainingProgramme.duplicate(id);
        this.loadProgrammes();
      } catch (err) {
        console.error('Error duplicating programme:', err);
      }
    },
    async openViewProgramme(programme) {
      this.loadingProgrammeDrills = true;
      this.viewingProgramme = { ...programme, drills: [] };
      try {
        const res = await TrainingProgramme.show(programme.id);
        if (res.data) {
          this.viewingProgramme = { ...res.data };
        }
      } catch (err) {
        console.error('Error loading programme:', err);
      } finally {
        this.loadingProgrammeDrills = false;
      }
    },
    // Drill picker helpers
    isDrillInProgramme(drillId) {
      return this.programmeLinkedDrills.some(d => d.id == drillId);
    },
    toggleDrillInProgramme(drill) {
      if (this.isDrillInProgramme(drill.id)) {
        this.programmeLinkedDrills = this.programmeLinkedDrills.filter(d => d.id !== drill.id);
      } else {
        this.programmeLinkedDrills = [...this.programmeLinkedDrills, drill];
      }
    },
    removeDrillFromProgramme(index) {
      this.programmeLinkedDrills = this.programmeLinkedDrills.filter((_, i) => i !== index);
    },
  },
};
</script>
<style scoped lang="scss">
/* ---- Drill PDF attachment ---- */
.pdf-uploading {
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
  color: #64748b;

  .spin {
    display: inline-block;
    animation: pdf-spin 0.8s linear infinite;
  }
}
@keyframes pdf-spin {
  to {
    transform: rotate(360deg);
  }
}
.pdf-attached {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin: 0.25rem 0 0.75rem;

  .pdf-attached-link {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 600;
    color: #dc2626;
    text-decoration: none;
    word-break: break-all;

    &:hover {
      text-decoration: underline;
    }
  }
}
.training-pdf-link {
  margin: 0.5rem 0 1rem;
}

/* ---- Library Header ---- */
.library-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  width: 100%;
}

.archive.training-plans.tab-content {
  padding: 1rem;
  /* Override the global .tabs-wrapper .tab-content flex rule so the
     drills/programmes sections span full width and their card grids
     can flow horizontally */
  display: block;
  width: 100%;
}

.search-filter {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;

  i.bi-search {
    position: absolute;
    left: 10px;
    color: #94a3b8;
    font-size: 0.8rem;
    pointer-events: none;
  }

  input {
    height: 36px;
    padding: 0 2rem 0 2.1rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.83rem;
    width: 220px;
    box-sizing: border-box;
    transition: border-color 0.2s;

    &:focus {
      border-color: #4299e1;
      outline: none;
      box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
    }
  }

  .clear-search {
    position: absolute;
    right: 6px;
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    font-size: 0.75rem;
    padding: 2px;
    display: flex;
    align-items: center;
  }
}

/* ---- Card Grid ---- */
.training-plan-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 400px), 1fr));
  gap: 1.5rem;
}

.library-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3000;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 8px;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);

  &.library-toast--success {
    background: #16a34a;
  }

  &.library-toast--error {
    background: #dc2626;
  }
}

.library-toast-enter-active,
.library-toast-leave-active {
  transition: opacity 0.25s, transform 0.25s;
}

.library-toast-enter-from,
.library-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

.programme-search {
  margin-bottom: 1rem;
}

.load-more-wrap {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

/* Card styles now live in the shared DrillCard component
   (src/components/DrillCard) */

/* ---- Empty State ---- */
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #718096;

  img {
    max-width: 200px;
    opacity: 0.6;
    margin-bottom: 1rem;
  }

  p {
    font-size: 0.95rem;
  }
}

/* ---- Modal Overlay ---- */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.session-modal-overlay {
  z-index: 1055;
}

.modal-dialog {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.session-detail-modal {
  max-width: 95vw;
  width: 95vw;
  max-height: 95vh;

  .modal-content {
    min-height: 85vh;
    display: flex;
    flex-direction: column;
  }

  .modal-body {
    max-height: 85vh;
    overflow-y: auto;
    flex: 1;
  }
}

.add-edit-modal {
  max-width: 700px;
  width: 100%;
  max-height: 95vh;

  .modal-body {
    max-height: 80vh;
    overflow-y: auto;
    padding: 1.5rem;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;

  h3 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
  }
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  color: #718096;
  line-height: 1;

  &:hover {
    color: #1a202c;
  }
}

.modal-body {
  padding: 1.5rem;
}

/* ---- Tabs ---- */
.tabs-wrapper .tab-content {
  height: 100%;
  width: 100%;
}

/* ---- Form Styling ---- */
.form__item {
  margin-bottom: 1.25rem;
}

.form__label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.9rem;
  color: #374151;
}

.add-edit-modal {

  .form__input,
  select,
  textarea {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.9rem;
    transition: border-color 0.2s;

    &:focus {
      border-color: #4299e1;
      outline: none;
      box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
    }
  }

  textarea {
    min-height: 80px;
    resize: vertical;
  }
}

.danger-outline {
  border-color: #dc3545 !important;
}

.assignment-mode-toggle {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.select-all-wrapper {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.user-select-wrapper {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.75rem;

  div {
    margin-bottom: 0.25rem;
  }

  label {
    margin-left: 0.5rem;
    cursor: pointer;
  }

  input[type="checkbox"] {
    cursor: pointer;
  }
}

.form-submit {
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

/* ---- Exercise Linking Section ---- */
.exercise-linking-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px dashed #dee2e6;

  .form__label {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    i {
      color: #007bff;
    }
  }
}

.linked-exercises-preview {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.no-exercises {
  text-align: center;
  padding: 1rem 0;

  p {
    color: #6c757d;
    margin: 0;
  }
}

.selected-exercises-list {
  margin-bottom: 1rem;
}

.selected-exercise-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;

  .exercise-info {
    flex: 1;

    .exercise-name {
      font-weight: 500;
      display: block;
    }

    .exercise-meta {
      font-size: 0.8rem;
      color: #6c757d;
    }
  }

  .btn-remove-exercise {
    background: none;
    border: none;
    color: #dc3545;
    font-size: 1.25rem;
    cursor: pointer;
    padding: 0 0.5rem;
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }
}

.btn-add-exercises {
  width: 100%;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

/* ---- Exercise Display (View Modal) ---- */
.linked-exercises-display {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #dee2e6;

  h4 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;

    i {
      color: #007bff;
    }
  }
}

.exercise-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.exercise-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;

  .exercise-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;

    .exercise-name {
      font-weight: 600;
      font-size: 1.05rem;
    }

    .badge {
      font-size: 0.7rem;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;

      &.badge-easy {
        background: #d4edda;
        color: #155724;
      }

      &.badge-intermediate {
        background: #fff3cd;
        color: #856404;
      }

      &.badge-advanced {
        background: #f8d7da;
        color: #721c24;
      }
    }
  }

  .exercise-card-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 0.75rem;

    .category {
      background: #e9ecef;
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
    }

    .sets-reps,
    .duration {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
  }

  .exercise-description {
    font-size: 0.9rem;
    color: #495057;
    margin-bottom: 0.75rem;
    line-height: 1.5;
  }

  .exercise-precautions {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    background: #fff3cd;
    border-radius: 4px;
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    color: #856404;
    margin-bottom: 0.75rem;

    i {
      flex-shrink: 0;
      margin-top: 2px;
    }
  }

  .exercise-video {
    margin-top: 0.5rem;

    .btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }
  }
}

/* ---- Tactical Boards Inline ---- */
.tactical-boards-inline {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;

  .tb-board-list {
    .tb-board-item {
      .tb-board-preview {
        :deep(.board-content) {
          .canvas-wrap {
            display: block;
            position: relative;
            max-height: 29rem;
            max-width: 20rem;
          }
        }

        :deep(.notes-panel) {
          flex: 1 1 0%;
          min-width: 0;
          margin-top: 0;
          z-index: 1000;
        }
      }
    }
  }
}

/* ---- Tactical Boards Tab ---- */
.tactical-boards-tab {
  padding: 16px 0;
  width: 100%;


  .tb-board-list {
    .tb-board-item {
      .tb-board-preview {
        .tactical-board-slide {
          :deep(.board-content) {
            .canvas-wrap {
              display: block;
              position: relative;
              max-height: 29rem;
              max-width: 20rem;
            }
          }

          :deep(.notes-panel) {
            flex: 0 0 100%;
            z-index: 1000;
          }
        }
      }
    }
  }
}


.tb-tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h4 {
    font-size: 1.05rem;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #1a1a2e;

    i {
      color: #667eea;
      font-size: 1.1rem;
    }
  }

  .btn {
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.85rem;
    padding: 6px 14px;
  }
}

.tb-empty-state {
  text-align: center;
  padding: 48px 24px;
  background: #f8f9fc;
  border-radius: 12px;
  border: 2px dashed #dee2e6;

  i {
    font-size: 2.5rem;
    display: block;
    margin-bottom: 12px;
    color: #667eea;
    opacity: 0.4;
  }

  p {
    margin-bottom: 16px;
    color: #6c757d;
    font-size: 0.95rem;
  }

  .btn {
    border-radius: 8px;
    font-weight: 500;
  }
}

.tb-board-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── tb-board-preview: canvas left, notes panel right ── */
.tb-board-preview {
  :deep(.board-content) {
    flex-direction: row;
    align-items: flex-start;
    flex-wrap: nowrap;
  }

  :deep(.canvas-wrap) {
    flex: 1 1 0%;
    min-width: 0;
    overflow: hidden;
  }

  /* Force the SVG to fit its container width so the full canvas is visible */
  :deep(.drawing-canvas) {
    width: 100%;
    height: auto;
    max-height: none;
  }

  :deep(.notes-panel) {
    width: 200px;
    flex: 0 0 200px;
    margin-top: 0;
    z-index: 1000;
  }

  :deep(.notes-display) {
    display: block;
    max-height: 380px;
    overflow-y: auto;
  }
}

@media (max-width: 768px) {
  .tb-board-preview {
    :deep(.board-content) {
      flex-direction: column;
    }

    :deep(.notes-panel) {
      width: 100%;
      flex: 0 0 auto;
      margin-top: 12px;
      z-index: 1000;
    }
  }
}

.tb-board-item {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }
}

.tb-board-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fc;
  border-bottom: 1px solid #e5e7eb;
}

.tb-board-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.tb-board-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1a1a2e;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    color: #667eea;
    font-size: 0.9rem;
  }
}

.tb-category-badge {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.tb-cat-training {
  background: #e0f2fe;
  color: #0369a1;
}

.tb-cat-rtp {
  background: #fce7f3;
  color: #be185d;
}

.tb-cat-set-piece {
  background: #fef3c7;
  color: #b45309;
}

.tb-cat-formation {
  background: #dbeafe;
  color: #1d4ed8;
}

.tb-cat-general {
  background: #f3f4f6;
  color: #4b5563;
}

.tb-board-preview {
  border-bottom: 1px solid #e5e7eb;
}

.tb-board-notes {
  padding: 10px 16px;
  font-size: 0.85rem;
  color: #555;
  background: #fafbfc;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  gap: 6px;

  i {
    color: #667eea;
    margin-top: 2px;
    flex-shrink: 0;
  }
}

.tb-remove-btn {
  border-radius: 6px;
  font-size: 0.8rem;
  padding: 4px 10px;
  white-space: nowrap;
}

/* ---- Tactical Board Viewer Modal ---- */
.tb-viewer-overlay {
  z-index: 1060;
}

.tb-viewer-dialog {
  width: 95vw;
  max-width: 95vw;
  height: 95vh;
  max-height: 95vh;
  margin: 2.5vh auto;
}

.tb-viewer-content {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tb-viewer-content .modal-header {
  background: #0f172a;
  border-bottom: 1px solid #334155;
  padding: 14px 20px;
}

.tb-viewer-content .modal-header h3 {
  color: #e2e8f0;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tb-viewer-content .modal-header h3 i {
  color: #667eea;
}

.tb-viewer-content .close-button {
  color: #94a3b8;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}

.tb-viewer-content .close-button:hover {
  color: #e2e8f0;
}

.tb-viewer-body {
  padding: 0;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.tb-viewer-body :deep(.tactical-board-wrapper) {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.tb-viewer-body :deep(.board-view-mode) {
  flex: 1;
  display: flex;
  flex-direction: column;
  border-radius: 0;
}

.tb-viewer-body :deep(.board-preview-container) {
  flex: 1;
  min-height: 0;
}

.tb-viewer-body :deep(.board-actions) {
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  background: #0f172a;
  border-top: 1px solid #334155;
  padding: 12px 20px;
  z-index: 5;
}

/* ---- Responsive ---- */
@media (max-width: 768px) {
  .training-plan-grid,
  .programme-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .library-header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-filter {
    flex-direction: column;
  }

  .search-box input {
    width: 100%;
    height: 36px;
  }

  .session-detail-modal {
    max-width: 100vw;
    width: 100vw;
  }

  .add-edit-modal {
    max-width: 100vw;
    width: 100vw;
  }

  .filter-row {
    flex-direction: column;
  }

  .form-classification-grid {
    grid-template-columns: 1fr !important;
  }
}

/* ---- Filter Toggle Button ---- */
.filter-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  font-size: 0.83rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  position: relative;
  box-sizing: border-box;
  white-space: nowrap;
}

.filter-toggle-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
  color: #111827;
}

.filter-toggle-btn.active {
  background: #eff6ff;
  border-color: #93c5fd;
  color: #2563eb;
}

.filter-count-badge {
  background: #f97316;
  color: white;
  font-size: 0.55rem;
  font-weight: 700;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -4px;
  right: -4px;
}

/* ---- Sort component alignment ---- */
.search-filter :deep(.sortBy) {
  margin: 0;
  display: flex;
  align-items: center;
}

.search-filter :deep(.form-select) {
  height: 36px;
  margin-bottom: 0 !important;
  padding: 0 2rem 0 0.65rem;
  font-size: 0.83rem;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-sizing: border-box;
  background-color: #fff;
  cursor: pointer;

  &:focus {
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
    outline: none;
  }
}

/* ---- Filter Bar ---- */
.filter-bar {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 1rem;
  width: 100%;
}

.filter-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-item {
  flex: 1;
  min-width: 140px;
  max-width: 200px;
}

.filter-item label {
  display: block;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.filter-item select {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.06);
  color: #515357;
  font-size: 0.78rem;
  outline: none;
  cursor: pointer;
  transition: border-color 0.15s;
}

.filter-item select:focus {
  border-color: #667eea;
}

.filter-item select:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.clear-filters-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  padding: 4px 12px;
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  font-size: 0.72rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.clear-filters-btn:hover {
  background: rgba(239, 68, 68, 0.2);
}

/* ---- Card Tags/Badges ---- */
.card-meta-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin: 6px 0 8px;

  .meta-item {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.7rem;
    color: #6b7280;
    background: #f3f4f6;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 500;

    i {
      font-size: 0.65rem;
    }
  }
}

.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 4px 0 6px;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 4px;
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.6;
}

.tag-type {
  border: 1px solid;
}

.tag-category {
  background: rgba(102, 126, 234, 0.15);
  color: #818cf8;
}

.tag-level {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

/* ---- Form Classification Grid ---- */
.form-classification-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
  margin-bottom: 1rem;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
}

.form-classification-grid .form__item {
  margin-bottom: 0;
}

.form-classification-grid .form__label {
  font-size: 0.72rem;
  margin-bottom: 3px;
}

.form-classification-grid select {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  font-size: 0.78rem;
  outline: none;
}

.form-classification-grid select:focus {
  border-color: #667eea;
}

.form-classification-grid select:disabled {
  opacity: 0.4;
}

/* ──────────────────────────────────────────────────────────────────────────
   Library Top Bar (action button row)
─────────────────────────────────────────────────────────────────────────── */
.library-top-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 0.75rem;
  min-height: 38px;
}

/* ──────────────────────────────────────────────────────────────────────────
   Library Type Tabs (Programmes / Drills)
─────────────────────────────────────────────────────────────────────────── */
.library-type-tabs {
  display: flex;
  width: 100%;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e2e8f0;
}

.lib-tab {
  flex: 1;
  background: none;
  border: none;
  padding: 0.65rem 1rem;
  font-size: 0.88rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    color: #334155;
    background: #f8fafc;
  }

  &.active {
    color: #26499d;
    border-bottom-color: #26499d;
    background: #f5f3ff;
  }
}

/* ──────────────────────────────────────────────────────────────────────────
   Programme Cards
─────────────────────────────────────────────────────────────────────────── */
.programme-category-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.category-pill {
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #eff6ff;
  }

  &.active {
    background: #3b82f6;
    color: #fff;
    border-color: #3b82f6;
  }
}

.prog-badge-category {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.programme-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.programme-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.1s;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  .card-actions {
    display: flex;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    border-top: 1px solid #e2e8f0;
    justify-content: flex-end;
    background: #f8fafc;
  }
}

.programme-card-body {
  padding: 1.25rem;
  flex: 1;
}

.programme-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.4rem;
}

.programme-description {
  font-size: 0.83rem;
  color: #64748b;
  margin: 0 0 0.75rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.programme-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 0.5rem;
}

.prog-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 20px;
  background: #eff6ff;
  color: #3b82f6;
  border: 1px solid #bfdbfe;

  i {
    font-size: 0.7rem;
  }
}

.equipment-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 0.75rem;
}

.equipment-chip {
  font-size: 0.68rem;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
}

.programme-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f5f9;
}

.drill-count-pill {
  font-size: 0.75rem;
  font-weight: 600;
  color: #26499d;
  display: flex;
  align-items: center;
  gap: 4px;
}

.programme-created-by {
  font-size: 0.72rem;
  color: #94a3b8;
}

/* ──────────────────────────────────────────────────────────────────────────
   Equipment checkbox group
─────────────────────────────────────────────────────────────────────────── */
.equipment-checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}

.equipment-checkbox-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 20px;
  background: #fff;
  color: #475569;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  user-select: none;

  /* Hide native checkbox — the pill itself is the visual control */
  input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
  }

  /* Custom indicator dot */
  &::before {
    content: '';
    display: inline-block;
    width: 13px;
    height: 13px;
    border: 1.5px solid #cbd5e1;
    border-radius: 50%;
    flex-shrink: 0;
    transition:
      background 0.15s,
      border-color 0.15s;
  }

  &:hover {
    border-color: #a5b4fc;
    background: #f5f3ff;
    color: #4338ca;

    &::before {
      border-color: #a5b4fc;
    }
  }

  &:has(input:checked) {
    background: #26499d;
    border-color: #26499d;
    color: #fff;
    box-shadow: 0 2px 6px rgba(79, 70, 229, 0.35);

    &::before {
      content: '✓';
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      font-size: 9px;
      font-weight: 700;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

/* ──────────────────────────────────────────────────────────────────────────
   Programme Drill list (inside create/edit modal)
─────────────────────────────────────────────────────────────────────────── */
.programme-drills-list {
  margin-bottom: 0.75rem;
}

.programme-drill-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 6px;
}

.drill-order {
  font-size: 0.72rem;
  font-weight: 700;
  color: #94a3b8;
  min-width: 18px;
  text-align: center;
}

.drill-name {
  flex: 1;
  font-size: 0.85rem;
  color: #1e293b;
  font-weight: 500;
}

.no-drills-msg {
  font-size: 0.82rem;
  color: #94a3b8;
  margin: 0.25rem 0 0.75rem;
}

.empty-state-text {
  text-align: center;
  color: #94a3b8;
  padding: 2rem 0;
}

/* ──────────────────────────────────────────────────────────────────────────
   Created-by info
─────────────────────────────────────────────────────────────────────────── */
.created-by-info {
  font-size: 0.78rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
